import { dissoc, head, last, length, map, path, pipe, values, when, pathEq, ifElse, filter as filterRamda } from 'ramda';
import { sumBy, groupBy, reject, filter } from 'lodash/fp';
import { createSelector } from 'reselect';
import {
  sumPricesForTip,
} from '../helpers';
import { mapEntries } from '../../common/helpers/functional';
import * as barberSelectors from '../../main/barbers/selector';
import { UNASSIGNED_BARBER } from '../constants';
import {
  filterServiceAppointmentProduct,
  filterServiceOrAppointment,
  getItems,
  includeProductInTip,
} from '../validated/selectors';
import { PAYMENT_TYPE } from '../../main/payment/constants';

export const getHasMore = pipe(
  path(['register', 'tipping', 'barbers', 'next']),
  length,
  Boolean,
);

const transformTippingBarber = state =>
  mapEntries((id, items) => {
    const barber = barberSelectors.getById(state, id) || items[0].barber;
    return {
      id: barber.id,
      firstName: barber.firstName,
      photo: path(['photos', 0, 'thumbnail'], barber),
      priceForTip: sumPricesForTip(items),
    };
  });

export const getTippingBarbers = state => pipe(
  getItems,
  groupBy('barberId'),
  map(ifElse(
    includeProductInTip(state),
    filterServiceAppointmentProduct,
    filterServiceOrAppointment,
  )),
  filterRamda(length),
  dissoc(UNASSIGNED_BARBER),
  dissoc(null),
  transformTippingBarber(state),
)(state);

export const tips = path(['register', 'tipping', 'tips']);
const stripPaymentId = when(pathEq(['type'], PAYMENT_TYPE.cash), dissoc('paymentId'));
export const getTips = createSelector(
  tips,
  pipe(values, reject({ amount: 0 }), map(stripPaymentId)),
);

export const getTotalTips = pipe(
  getTips,
  sumBy('amount')
);

export const getCurrentBarber = path(['register', 'tipping', 'barbers', 'current']);

export const getPreviousBarber = pipe(
  path(['register', 'tipping', 'barbers', 'prev']),
  last,
);

export const getNextBarber = pipe(
  path(['register', 'tipping', 'barbers', 'next']),
  head,
);

const filterPaymentsByShopSettings = ({ cashTipsEnabled, giftCardTipsEnabled, readerTipsEnabled }) =>
  reject((payment) => {
    switch (payment.type) {
      case PAYMENT_TYPE.cash:
        return !cashTipsEnabled;
      case PAYMENT_TYPE.giftCard:
        return !giftCardTipsEnabled;
      case PAYMENT_TYPE.reader:
      case PAYMENT_TYPE.readerless:
      case PAYMENT_TYPE.card:
        return !readerTipsEnabled;
      default:
        return false;
    }
  });

const adjustGiftCardPayments = tipItems => pipe(
  map((payment) => {
    if (payment.type !== PAYMENT_TYPE.giftCard) return payment;

    const tipAmount = pipe(
      filter({ paymentId: payment.id }),
      sumBy('amount'),
    )(tipItems);

    return {
      ...payment,
      balance: payment.balance - payment.amount - tipAmount,
    };
  }),
);

export const getTippingPayments = (payments, tipItems, { cashTipsEnabled, giftCardTipsEnabled, readerTipsEnabled }) =>
  pipe(
    filterPaymentsByShopSettings({ cashTipsEnabled, giftCardTipsEnabled, readerTipsEnabled }),
    adjustGiftCardPayments(tipItems, giftCardTipsEnabled),
  )(payments);

export const getProcessing = path(['register', 'tipping', 'processing']);

export const getAdjustments = path(['register', 'tipping', 'adjustments']);
